import { UserManagementIcon } from 'core/components/Icons'
import { Badge, BadgeTone } from 'core/components/ui/Badge'
import { Column, Table } from 'core/components/ui/Table'
import React from 'react'
import { User, UserRole, UserStatus } from '../../domain/entities/User'

interface UserTableProps {
  users: User[]
  loading: boolean
  onEdit: (user: User) => void
  onToggleStatus: (user: User) => void
  onResetPassword?: (user: User) => void
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  itemsPerPage: number
  canManage?: boolean
}

const roleTones: Record<UserRole, BadgeTone> = {
  admin: 'danger',
  cxo: 'primary',
  supervisor: 'secondary',
  finance: 'info',
  account_manager: 'success',
  customer_support: 'warning',
  fpo: 'neutral',
  vlcc: 'neutral',
  retailer: 'neutral',
}

const statusTones: Record<UserStatus, BadgeTone> = {
  active: 'success',
  inactive: 'danger',
  pending_kyc: 'warning',
  kyc_rejected: 'danger',
}

// Only active/inactive have a well-defined toggle (flip one to the other) — pending_kyc/kyc_rejected
// accounts are handled through the KYC flow, so their status badge is shown but isn't clickable here.
const isToggleableStatus = (status: UserStatus) => status === 'active' || status === 'inactive'

export const UserTable: React.FC<UserTableProps> = ({
  users,
  loading,
  onEdit,
  onToggleStatus,
  onResetPassword,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  canManage = false,
}) => {
  const columns: Column<User>[] = [
    {
      key: 'name',
      label: 'Name',
      render: (row) => <span className="font-semibold text-text-primary">{row.name}</span>,
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (row) => <span className="text-text-primary">{row.phone}</span>,
    },
    {
      key: 'email',
      label: 'Email',
      render: (row) => <span className="text-text-primary">{row.email || '—'}</span>,
    },
    {
      key: 'role',
      label: 'Role',
      render: (row) => (
        <Badge tone={roleTones[row.role]} variant="subtle" className="capitalize">
          {row.role.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        const badge = (
          <Badge tone={statusTones[row.status]} variant="subtle" className="capitalize">
            {row.status.replace('_', ' ')}
          </Badge>
        )
        if (!canManage || !isToggleableStatus(row.status)) return badge
        return (
          <button
            onClick={() => onToggleStatus(row)}
            className="focus:outline-none select-none active:scale-95 transition-transform"
          >
            {badge}
          </button>
        )
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-center',
      render: (row) => {
        if (!canManage) return <span className="text-xs text-text-secondary italic"></span>
        const isInternalRole = [
          'admin',
          'cxo',
          'supervisor',
          'finance',
          'account_manager',
          'customer_support',
        ].includes(row.role)
        return (
          <div className="flex items-center justify-center gap-2">
            {isInternalRole && onResetPassword && (
              <button
                onClick={() => onResetPassword(row)}
                title="Reset Password"
                aria-label="Reset Password"
                className="p-1.5 rounded-btn hover:bg-bg text-text-secondary hover:text-primary transition-colors border border-border/50 hover:border-border dark:border-[rgba(255,255,255,0.14)] dark:hover:border-[rgba(255,255,255,0.28)]"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </button>
            )}
            <button
              onClick={() => onEdit(row)}
              title="Edit User"
              aria-label="Edit User"
              className="p-1.5 rounded-btn hover:bg-bg text-text-secondary hover:text-text-primary transition-colors border border-border/50 hover:border-border dark:border-[rgba(255,255,255,0.14)] dark:hover:border-[rgba(255,255,255,0.28)]"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <Table
      columns={columns}
      data={users}
      loading={loading}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
        totalItems,
        itemsPerPage,
      }}
      emptyMessage="No user profiles match your filter criteria."
      emptyIcon={<UserManagementIcon size={20} className="opacity-60" />}
    />
  )
}
